import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { type ApiError, addActionNameToMutationResult, useAxiosInstance } from 'shared/hooks/utils';
import { z } from 'zod/v4';

const DeleteTemplateBodiesVariablesSchema = z.object({
  templateId: z.string(),
  bodyIds: z.array(z.string()),
});

export type DeleteTemplateBodiesVariables = z.infer<typeof DeleteTemplateBodiesVariablesSchema>;

async function deleteTemplateBodiesFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: DeleteTemplateBodiesVariables,
) {
  const { templateId, bodyIds } = DeleteTemplateBodiesVariablesSchema.parse(variables);
  const instance = await axiosInstance;

  return Promise.all(
    bodyIds.map(bodyId => instance.delete(`/templates/${templateId}/bodies/${bodyId}`)),
  );
}

export function useDeleteTemplateBodies() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<unknown, ApiError, DeleteTemplateBodiesVariables>({
    mutationFn: variables => deleteTemplateBodiesFn(axiosInstance, variables),
    onSuccess: (_data, { templateId }) => {
      client.invalidateQueries({ queryKey: ['template-v2', templateId] });
      client.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  return addActionNameToMutationResult<
    unknown,
    ApiError,
    DeleteTemplateBodiesVariables,
    'deleteTemplateBodies'
  >('deleteTemplateBodies', mutation);
}
